import {
  getAccessesForResource,
  type RegistrationAccessIndex,
  type RegistrationAccessRow
} from "../registration/access"

export type CoverPageContinueMode = "none" | "single" | "multiple"

export type CoverPageContinueOptions = {
  /**
   * True when the caller permits creating/joining another (Private Cohort Offer
   * Available, or header multi-offer scroll-to-offers).
   */
  canCreateAnother?: boolean
  /**
   * When true, only include RegistrationAccess for `cohortId`. Used by Join
   * Cohort Offer cards so Private Cohort memberships on the same resource do
   * not light up Continue on an Offer the user never joined. If `cohortId` is
   * missing, match nothing.
   */
  scopeToCohort?: boolean
  /** Cohort to match when `scopeToCohort` is true. */
  cohortId?: number | null
  /**
   * When true, only include RegistrationAccess created through
   * `resourceLicenseOfferingId`. Used by Private Cohort Offer cards so
   * on-demand / Join Cohort memberships on the same resource do not light up
   * Continue. If the offering id is missing, match nothing.
   */
  scopeToResourceLicenseOffering?: boolean
  /** Licensing relationship to match when `scopeToResourceLicenseOffering`. */
  resourceLicenseOfferingId?: number | null
}

export type CoverPageContinueState = {
  /** Resumable RegistrationAccess rows matched for this Continue context. */
  accesses: RegistrationAccessRow[]
  mode: CoverPageContinueMode
  canCreateAnother: boolean
  /** Single-access meta verb: Created for the creator, Joined otherwise. */
  singleMetaVerb: "created" | "joined" | null
}

/**
 * Resumable RegistrationAccess rows for a resource. Excludes archived/expired;
 * includes subscription_expired (Home-equivalent destination handles that flow).
 * Optionally scoped to a single cohort (Join Cohort) or licensing relationship
 * (Private Cohort Offer cards).
 */
export function getResumableAccessesForResource(
  index: RegistrationAccessIndex,
  resourceId: number | null | undefined,
  {
    scopeToCohort = false,
    cohortId,
    scopeToResourceLicenseOffering = false,
    resourceLicenseOfferingId
  }: Pick<
    CoverPageContinueOptions,
    | "scopeToCohort"
    | "cohortId"
    | "scopeToResourceLicenseOffering"
    | "resourceLicenseOfferingId"
  > = {}
): RegistrationAccessRow[] {
  if (resourceId == null) return []

  if (scopeToCohort && cohortId == null) return []
  if (scopeToResourceLicenseOffering && resourceLicenseOfferingId == null) {
    return []
  }

  let rows = getAccessesForResource(index, resourceId, { resumableOnly: true })
  if (scopeToCohort) {
    rows = rows.filter((row) => row.cohort_id === cohortId)
  }
  if (scopeToResourceLicenseOffering) {
    rows = rows.filter(
      (row) => row.resource_license_offering_id === resourceLicenseOfferingId
    )
  }

  return rows.slice().sort((a, b) => {
    // Newest first so a stable primary access is predictable.
    return new Date(b.created).getTime() - new Date(a.created).getTime()
  })
}

export function getCoverPageContinueState(
  index: RegistrationAccessIndex,
  resourceId: number | null | undefined,
  {
    canCreateAnother = false,
    scopeToCohort = false,
    cohortId,
    scopeToResourceLicenseOffering = false,
    resourceLicenseOfferingId
  }: CoverPageContinueOptions = {}
): CoverPageContinueState {
  const accesses = getResumableAccessesForResource(index, resourceId, {
    scopeToCohort,
    cohortId,
    scopeToResourceLicenseOffering,
    resourceLicenseOfferingId
  })
  const mode: CoverPageContinueMode =
    accesses.length === 0
      ? "none"
      : accesses.length === 1
      ? "single"
      : "multiple"
  const primary = accesses[0] ?? null

  return {
    accesses,
    mode,
    canCreateAnother: mode !== "none" && canCreateAnother,
    singleMetaVerb:
      mode === "single" ? (primary?.is_creator ? "created" : "joined") : null
  }
}

/** Whether Continue meta should render under the CTA. */
export function coverPageContinueShowsMeta(
  state: CoverPageContinueState
): boolean {
  return state.mode !== "none"
}

/**
 * Whether the Continue meta block needs a second line for "Create another?".
 * Only the single-access layout places that link under meta (multi puts it
 * in the menu).
 */
export function coverPageContinueShowsCreateAnotherMeta(
  state: CoverPageContinueState
): boolean {
  return state.mode === "single" && state.canCreateAnother
}
